// Firebase configuration and initialization
import { buildKakaoRedirectUri } from "@shared/config";
import { rememberPostLoginPath } from "./auth-navigation";
import { FirebaseApp, initializeApp } from "firebase/app";
import {
  Auth,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  User,
} from "firebase/auth";
import { Firestore, getFirestore } from "firebase/firestore";

type RuntimeEnv = Record<string, string | undefined>;

const fallbackEnv: RuntimeEnv = {
  VITE_FIREBASE_API_KEY: "AIzaSyDSBH-SzvS3tklgP_ngP9iqRk3R_hulpW0",
  VITE_FIREBASE_AUTH_DOMAIN: "ozknews-d4a88.firebaseapp.com",
  VITE_FIREBASE_PROJECT_ID: "ozknews-d4a88",
  VITE_FIREBASE_STORAGE_BUCKET: "ozknews-d4a88.appspot.com",
  VITE_FIREBASE_MESSAGING_SENDER_ID: "88800816901",
  VITE_FIREBASE_APP_ID: "1:88800816901:web:f710cbfe94ec616d44d16c",
  VITE_KAKAO_JAVASCRIPT_KEY: "950032c3e5e514613340055293950b88",
  FIREBASE_API_KEY: "AIzaSyDSBH-SzvS3tklgP_ngP9iqRk3R_hulpW0",
  FIREBASE_AUTH_DOMAIN: "ozknews-d4a88.firebaseapp.com",
  FIREBASE_PROJECT_ID: "ozknews-d4a88",
  FIREBASE_STORAGE_BUCKET: "ozknews-d4a88.appspot.com",
  FIREBASE_MESSAGING_SENDER_ID: "88800816901",
  FIREBASE_APP_ID: "1:88800816901:web:f710cbfe94ec616d44d16c",
  KAKAO_JAVASCRIPT_KEY: "950032c3e5e514613340055293950b88",
};

const buildTimeEnv: RuntimeEnv =
  typeof import.meta !== "undefined"
    ? {
        VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
        VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        VITE_FIREBASE_STORAGE_BUCKET: import.meta.env
          .VITE_FIREBASE_STORAGE_BUCKET,
        VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env
          .VITE_FIREBASE_MESSAGING_SENDER_ID,
        VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
        VITE_KAKAO_JAVASCRIPT_KEY: import.meta.env.VITE_KAKAO_JAVASCRIPT_KEY,
        FIREBASE_API_KEY: import.meta.env.FIREBASE_API_KEY,
        FIREBASE_AUTH_DOMAIN: import.meta.env.FIREBASE_AUTH_DOMAIN,
        FIREBASE_PROJECT_ID: import.meta.env.FIREBASE_PROJECT_ID,
        FIREBASE_STORAGE_BUCKET: import.meta.env.FIREBASE_STORAGE_BUCKET,
        FIREBASE_MESSAGING_SENDER_ID: import.meta.env
          .FIREBASE_MESSAGING_SENDER_ID,
        FIREBASE_APP_ID: import.meta.env.FIREBASE_APP_ID,
        KAKAO_JAVASCRIPT_KEY: import.meta.env.KAKAO_JAVASCRIPT_KEY,
      }
    : {};

const importMetaEnv: RuntimeEnv =
  typeof import.meta !== "undefined" && (import.meta as any)?.env
    ? {
        ...((import.meta as any).env as RuntimeEnv),
        ...buildTimeEnv,
      }
    : buildTimeEnv;

const runtimeWindowEnv: RuntimeEnv =
  typeof window !== "undefined" && (window as any)?.__OZK_ENV__
    ? ((window as any).__OZK_ENV__ as RuntimeEnv)
    : {};

const fallbackKeysUsed = new Set<string>();

function readEnv(...keys: string[]): string | undefined {
  for (const key of keys) {
    const value =
      importMetaEnv[key] ?? runtimeWindowEnv[key] ?? fallbackEnv[key];
    if (typeof value === "string" && value.length > 0) {
      if (
        fallbackEnv[key] !== undefined &&
        importMetaEnv[key] === undefined &&
        runtimeWindowEnv[key] === undefined
      ) {
        fallbackKeysUsed.add(key);
      }
      return value;
    }
  }
  return undefined;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Firebase
 * ────────────────────────────────────────────────────────────────────────── */
export const firebaseConfig = {
  apiKey: readEnv("VITE_FIREBASE_API_KEY", "FIREBASE_API_KEY"),
  authDomain: readEnv("VITE_FIREBASE_AUTH_DOMAIN", "FIREBASE_AUTH_DOMAIN"),
  projectId: readEnv("VITE_FIREBASE_PROJECT_ID", "FIREBASE_PROJECT_ID"),
  storageBucket: readEnv(
    "VITE_FIREBASE_STORAGE_BUCKET",
    "FIREBASE_STORAGE_BUCKET"
  ),
  messagingSenderId: readEnv(
    "VITE_FIREBASE_MESSAGING_SENDER_ID",
    "FIREBASE_MESSAGING_SENDER_ID"
  ),
  appId: readEnv("VITE_FIREBASE_APP_ID", "FIREBASE_APP_ID"),
};

if (fallbackKeysUsed.size > 0) {
  console.warn(
    `⚠️ Firebase config using baked-in defaults for: ${Array.from(
      fallbackKeysUsed
    ).join(", ")}`
  );
}

let app: FirebaseApp | undefined;
let auth: Auth | undefined;
let db: Firestore | undefined;

try {
  if (firebaseConfig.apiKey && firebaseConfig.projectId) {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
    console.log("✅ Firebase initialized successfully", {
      apiKey: firebaseConfig.apiKey
        ? `${firebaseConfig.apiKey.substring(0, 10)}...`
        : "missing",
      authDomain: firebaseConfig.authDomain,
      projectId: firebaseConfig.projectId,
    });
  } else {
    console.warn("⚠️ Firebase config missing. Auth features will be disabled.");
  }
} catch (error) {
  console.error("❌ Firebase initialization error:", error);
  console.warn(
    "Firebase is not initialized. Authentication features will be disabled."
  );
}

export { auth, db };
export function ensureAuthInstance(): Auth {
  if (!auth) {
    throw new Error("Firebase not initialized");
  }
  return auth;
}

export async function loginWithCustomToken(customToken: string): Promise<User> {
  const authInstance = ensureAuthInstance();

  try {
    console.log("🔑 Attempting to sign in with custom token...");
    const credential = await signInWithCustomToken(authInstance, customToken);
    console.log(
      "✅ Custom token sign-in successful, user:",
      credential.user.uid
    );

    try {
      await credential.user.reload();
      console.log("✅ User reloaded successfully");
    } catch (error) {
      console.warn(
        "⚠️ Failed to reload Firebase user after custom token sign-in:",
        error
      );
      // reload 실패는 치명적이지 않으므로 계속 진행
    }

    return credential.user;
  } catch (error: any) {
    console.error("❌ signInWithCustomToken failed:", error);
    console.error("Error code:", error?.code);
    console.error("Error message:", error?.message);
    console.error("Error details:", error);

    // 더 자세한 에러 메시지 제공
    let errorMessage = "Firebase 로그인에 실패했습니다";
    if (error?.code === "auth/invalid-custom-token") {
      errorMessage = "유효하지 않은 인증 토큰입니다. 다시 로그인해 주세요.";
    } else if (error?.code === "auth/custom-token-mismatch") {
      errorMessage = "인증 토큰이 프로젝트와 일치하지 않습니다.";
    } else if (error?.code === "auth/user-disabled") {
      errorMessage = "사용자 계정이 비활성화되었습니다.";
    } else if (error?.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
}

export async function getCurrentUser(
  forceReload = false
): Promise<User | null> {
  const authInstance = ensureAuthInstance();
  const existing = authInstance.currentUser;

  if (existing) {
    if (forceReload) {
      try {
        await existing.reload();
      } catch (error) {
        console.warn("⚠️ Failed to reload Firebase user:", error);
      }
    }
    return existing;
  }

  return new Promise<User | null>((resolve) => {
    const unsubscribe = onAuthStateChanged(
      authInstance,
      async (firebaseUser) => {
        unsubscribe();
        if (firebaseUser && forceReload) {
          try {
            await firebaseUser.reload();
          } catch (error) {
            console.warn(
              "⚠️ Failed to reload Firebase user in listener:",
              error
            );
          }
        }
        resolve(firebaseUser);
      }
    );
  });
}

export async function requireCurrentUser(forceReload = false): Promise<User> {
  const user = await getCurrentUser(forceReload);
  if (!user) {
    throw new Error("Firebase user is not authenticated");
  }
  return user;
}

export async function getIdToken(forceRefresh = false): Promise<string | null> {
  console.log(
    "%c[Firebase] 🔐 getIdToken called (forceRefresh:",
    "color: purple; font-weight: bold",
    forceRefresh,
    ")"
  );
  const user = await getCurrentUser();
  console.log(
    "%c[Firebase] 👤 getCurrentUser result:",
    "color: purple",
    user ? `user (email: ${user.email})` : "null"
  );

  if (!user) {
    console.warn(
      "%c[Firebase] ⚠️ No user found, returning null token",
      "color: orange; font-weight: bold"
    );
    return null;
  }

  try {
    console.log(
      "%c[Firebase] 📞 Calling user.getIdToken(",
      "color: purple",
      forceRefresh,
      ")..."
    );
    const token = await user.getIdToken(forceRefresh);
    console.log(
      "%c[Firebase] ✅ Got ID token (length:",
      "color: green; font-weight: bold",
      token?.length || 0,
      ")"
    );
    return token;
  } catch (error) {
    console.error(
      "%c⚠️ Failed to obtain Firebase ID token:",
      "color: red; font-weight: bold",
      error
    );
    console.error(
      "%cError details:",
      "color: red",
      error instanceof Error ? error.message : String(error)
    );
    return null;
  }
}

export async function requireIdToken(forceRefresh = false): Promise<string> {
  const token = await getIdToken(forceRefresh);
  if (!token) {
    throw new Error("Authentication required");
  }
  return token;
}

const googleProvider = auth ? new GoogleAuthProvider() : null;

export function signInWithGoogle() {
  if (!auth || !googleProvider) {
    return Promise.reject(new Error("Firebase not initialized"));
  }

  // Use popup instead of redirect for better error handling
  // AuthContext's onAuthStateChanged will handle /api/me automatically
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });

  console.log("[Firebase] Starting Google sign-in with popup...");
  return signInWithPopup(auth, provider)
    .then((result) => {
      console.log("[Firebase] Google sign-in successful:", {
        uid: result.user.uid,
        email: result.user.email,
      });
      // Don't call /api/me here - AuthContext will handle it via onAuthStateChanged
      return result;
    })
    .catch((error) => {
      console.error("[Firebase] Google sign-in error:", {
        code: error.code,
        message: error.message,
      });
      throw error;
    });
}

export function signInWithEmail(email: string, password: string) {
  if (!auth) {
    console.error("[Firebase] Auth not initialized");
    return Promise.reject(new Error("Firebase not initialized"));
  }

  console.log("[Firebase] Attempting email/password sign in:", {
    email: email.substring(0, 5) + "...",
    hasPassword: !!password,
  });

  return signInWithEmailAndPassword(auth, email, password)
    .then((userCredential) => {
      console.log("[Firebase] Sign in successful:", {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
      });
      return userCredential;
    })
    .catch((error) => {
      console.error("[Firebase] Sign in error:", {
        code: error.code,
        message: error.message,
      });
      throw error;
    });
}

export function signUpWithEmail(email: string, password: string) {
  if (!auth) {
    console.error("[Firebase] Auth not initialized");
    return Promise.reject(new Error("Firebase not initialized"));
  }

  console.log("[Firebase] Attempting email/password sign up:", {
    email: email.substring(0, 5) + "...",
    hasPassword: !!password,
  });

  return createUserWithEmailAndPassword(auth, email, password)
    .then((userCredential) => {
      console.log("[Firebase] Sign up successful:", {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
      });
      return userCredential;
    })
    .catch((error) => {
      console.error("[Firebase] Sign up error:", {
        code: error.code,
        message: error.message,
      });
      throw error;
    });
}

export function signOut() {
  if (!auth) {
    return Promise.reject(new Error("Firebase not initialized"));
  }
  return firebaseSignOut(auth);
}

export function resetPassword(email: string) {
  if (!auth) {
    console.error("[Firebase] Auth not initialized");
    return Promise.reject(new Error("Firebase not initialized"));
  }

  console.log("[Firebase] Sending password reset email:", {
    email: email.substring(0, 5) + "...",
  });

  // Action Code Settings: 리디렉션 URL 설정
  // Firebase Console에서 설정한 도메인으로 리디렉션
  const actionCodeSettings = {
    url:
      typeof window !== "undefined"
        ? `${window.location.origin}/login`
        : "https://ozknews.com/login",
    handleCodeInApp: false, // 이메일 링크를 앱에서 열지 않고 브라우저에서 열기
  };

  return sendPasswordResetEmail(auth, email, actionCodeSettings)
    .then(() => {
      console.log("[Firebase] Password reset email sent successfully", {
        redirectUrl: actionCodeSettings.url,
      });
    })
    .catch((error) => {
      console.error("[Firebase] Password reset error:", {
        code: error.code,
        message: error.message,
        error,
      });
      throw error;
    });
}

/* ──────────────────────────────────────────────────────────────────────────
 * Kakao (프런트는 '자바스크립트 키'만 사용)
 *  - JS 키: VITE_KAKAO_JAVASCRIPT_KEY    ← 프런트에서 사용 (노출 OK)
 *  - REST 키: 서버에서만 사용            ← 프런트에 두지 말 것!
 * ────────────────────────────────────────────────────────────────────────── */

declare global {
  interface Window {
    __OZK_ENV__?: RuntimeEnv;
    Kakao?: {
      init: (key: string) => void;
      isInitialized: () => boolean;
      Auth: {
        authorize: (opts: {
          redirectUri: string;
          scope?: string;
          state?: string;
          prompt?: string;
        }) => void;
        login?: (opts: any) => void;
      };
      API?: {
        request: (opts: any) => void;
      };
      Link?: any;
      Share?: {
        sendDefault: (opts: any) => void;
      };
    };
  }
}

const AUTO_FLAG = "OZK_AUTO_START_KAKAO";
const KAKAO_OAUTH_STATE_KEY = "OZK_KAKAO_OAUTH_STATE";
const KAKAO_JS_KEY = readEnv(
  "VITE_KAKAO_JAVASCRIPT_KEY",
  "KAKAO_JAVASCRIPT_KEY"
);

function getRedirectUri(): string {
  // 백엔드가 실제 콜백을 받는 엔드포인트(프로덕션/로컬 모두 동일 규칙)
  return buildKakaoRedirectUri();
}

/** Kakao SDK 초기화 (중복 호출 안전) */
export function initKakao() {
  if (!KAKAO_JS_KEY) {
    console.warn("⚠️ VITE_KAKAO_JAVASCRIPT_KEY is missing");
    return;
  }
  if (!window.Kakao) {
    console.warn("⚠️ Kakao SDK script not found on window");
    return;
  }
  if (!window.Kakao.isInitialized()) {
    window.Kakao.init(KAKAO_JS_KEY);
    console.log("✅ Kakao SDK initialized");
  }
}

/** iframe이 sandbox라면 top 이동이 막히므로, 새 탭으로 열도록 우회 */
function openSelfInNewTabForAuth(): boolean {
  try {
    const inIframe = window.self !== window.top;
    const sandboxed = !!(
      window.frameElement as HTMLIFrameElement | null
    )?.hasAttribute("sandbox");

    if (inIframe && sandboxed) {
      // 새 탭에서 자동으로 로그인 시작하도록 플래그 저장
      sessionStorage.setItem(AUTO_FLAG, "1");

      const url = new URL(window.location.href);
      url.searchParams.set("kakao", "1"); // 새 탭에서 감지용 파라미터
      const w = window.open(url.toString(), "_blank", "noopener");

      if (!w) {
        alert("팝업이 차단되었습니다. 새 탭으로 열기를 허용해 주세요.");
      }
      return true; // 현재 컨텍스트에서는 더 진행하지 않음
    }
  } catch {
    // ignore
  }
  return false;
}

/** 새 탭에서 자동으로 카카오 로그인 시작 (초기 마운트 시 호출) */
export function maybeAutoStartKakaoLogin() {
  try {
    const url = new URL(window.location.href);
    const shouldStart =
      sessionStorage.getItem(AUTO_FLAG) === "1" ||
      url.searchParams.get("kakao") === "1";

    if (shouldStart) {
      // 한 번만
      sessionStorage.removeItem(AUTO_FLAG);
      url.searchParams.delete("kakao");
      window.history.replaceState({}, "", url.toString());

      // SDK 준비 후 시작
      if (window.Kakao?.isInitialized?.()) {
        startKakaoLogin();
      } else {
        // SDK가 늦게 로드될 수 있으니 약간 지연
        setTimeout(() => startKakaoLogin(), 150);
      }
    }
  } catch {
    // ignore
  }
}

/** 카카오 로그인 시작 (Kakao.Auth.authorize 사용, JS 키 필요) */
export function startKakaoLogin(options?: {
  scope?: string;
  prompt?: string;
}): boolean {
  // sandbox iframe이면 새 탭으로 우회
  if (openSelfInNewTabForAuth()) return true;

  if (!KAKAO_JS_KEY) {
    console.error("❌ VITE_KAKAO_JAVASCRIPT_KEY is not configured");
    alert(
      "카카오 로그인 설정이 필요합니다. 관리자에게 문의하세요.\n\nVITE_KAKAO_JAVASCRIPT_KEY가 설정되지 않았거나 잘못되었습니다."
    );
    return false;
  }

  initKakao();

  if (!window.Kakao?.Auth?.authorize) {
    console.error("❌ window.Kakao.Auth.authorize not available");
    alert("카카오 SDK가 로드되지 않았습니다. 잠시 후 다시 시도해 주세요.");
    return false;
  }

  const redirectUri = getRedirectUri();
  rememberPostLoginPath();
  const state = crypto.randomUUID();
  sessionStorage.setItem(KAKAO_OAUTH_STATE_KEY, state);

  try {
    window.Kakao.Auth.authorize({
      redirectUri,
      scope: options?.scope ?? "profile_nickname,account_email",
      ...(options?.prompt ? { prompt: options.prompt } : {}),
      state,
    });
    return true;
  } catch (err) {
    console.error("❌ Kakao authorize error:", err);
    alert("카카오 로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.");
    return false;
  }
}

export function consumeKakaoOAuthState(receivedState: string | null): boolean {
  const expectedState = sessionStorage.getItem(KAKAO_OAUTH_STATE_KEY);
  sessionStorage.removeItem(KAKAO_OAUTH_STATE_KEY);
  return Boolean(receivedState && expectedState && receivedState === expectedState);
}

/** 기존 인터페이스 유지용 래퍼 */
export async function signInWithKakao(): Promise<void> {
  if (!startKakaoLogin()) {
    throw new Error("카카오 로그인을 시작하지 못했습니다.");
  }
}

/* (선택) 구형 login() 기반 레거시 — 유지하되 호출은 권장하지 않음 */
async function signInWithKakaoLegacy(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!auth) {
      reject(new Error("Firebase가 초기화되지 않았습니다"));
      return;
    }

    if (!window.Kakao?.Auth?.login) {
      reject(new Error("Kakao.Auth.login을 사용할 수 없습니다"));
      return;
    }

    window.Kakao.Auth.login({
      success: async (_authObj: any) => {
        try {
          window.Kakao?.API?.request({
            url: "/v2/user/me",
            success: async (response: any) => {
              const kakaoAccount = response.kakao_account;
              const profile = kakaoAccount?.profile ?? {};

              // 백엔드로 전달하여 Firebase Custom Token 발급
              const backendResponse = await fetch("/api/auth/kakao", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  kakaoId: response.id,
                  email: kakaoAccount?.email,
                  displayName: profile?.nickname,
                  photoURL: profile?.profile_image_url,
                }),
              });

              if (!backendResponse.ok)
                throw new Error("Firebase 토큰 생성 실패");

              const { customToken } = (await backendResponse.json()) as {
                customToken: string;
              };

              await loginWithCustomToken(customToken);
              resolve();
            },
            fail: () =>
              reject(new Error("카카오 사용자 정보를 가져오는데 실패했습니다")),
          });
        } catch (error) {
          reject(error);
        }
      },
      fail: () => reject(new Error("카카오 로그인에 실패했습니다")),
    });
  });
}

/* ──────────────────────────────────────────────────────────────────────────
 * Utilities
 * ────────────────────────────────────────────────────────────────────────── */
export function ensureHttps(url?: string | null): string | undefined {
  if (!url) return undefined;
  try {
    const u = new URL(url);
    if (u.protocol === "http:") u.protocol = "https:";
    return u.toString();
  } catch {
    return undefined;
  }
}

export function getSafeAvatar(url?: string | null): string {
  const httpsUrl = ensureHttps(url);
  return (
    httpsUrl ??
    "https://cdn.jsdelivr.net/gh/edent/SuperTinyIcons/images/svg/user.svg"
  );
}

export { getRedirectResult, onAuthStateChanged };
export type { User };
